/**
 * Workflow Live Runs Store
 *
 * Tracks active (running) runs per workflow for real-time UI updates.
 * Used to show pulsing indicators and live run counts in the workflow header.
 */

import { create } from "zustand";

/**
 * Information about an active run
 */
export interface ActiveRunInfo {
  runId: string;
  startedAt: string;
}

/**
 * Workflow Live Runs Store Interface
 */
interface WorkflowLiveRunsStore {
  // Map of workflowId -> Set of active runIds
  activeRuns: Map<string, Map<string, ActiveRunInfo>>;

  // Actions
  addActiveRun: (workflowId: string, runId: string) => void;
  removeActiveRun: (workflowId: string, runId: string) => void;
  clearWorkflowRuns: (workflowId: string) => void;

  // Selectors
  hasActiveRuns: (workflowId: string) => boolean;
  getActiveRunCount: (workflowId: string) => number;

  // Reset
  reset: () => void;
}

const initialState = {
  activeRuns: new Map<string, Map<string, ActiveRunInfo>>(),
};

/**
 * Workflow Live Runs Store
 */
export const useWorkflowLiveRunsStore = create<WorkflowLiveRunsStore>((set, get) => ({
  ...initialState,

  addActiveRun: (workflowId: string, runId: string) => {
    set(state => {
      const newActiveRuns = new Map(state.activeRuns);
      const workflowRuns = newActiveRuns.get(workflowId) ?? new Map<string, ActiveRunInfo>();

      // Don't add if already exists
      if (workflowRuns.has(runId)) {
        return state;
      }

      workflowRuns.set(runId, {
        runId,
        startedAt: new Date().toISOString(),
      });
      newActiveRuns.set(workflowId, workflowRuns);

      return { activeRuns: newActiveRuns };
    });
  },

  removeActiveRun: (workflowId: string, runId: string) => {
    set(state => {
      const newActiveRuns = new Map(state.activeRuns);
      const workflowRuns = newActiveRuns.get(workflowId);

      if (!workflowRuns || !workflowRuns.has(runId)) {
        return state;
      }

      workflowRuns.delete(runId);

      // Clean up empty workflow entries
      if (workflowRuns.size === 0) {
        newActiveRuns.delete(workflowId);
      } else {
        newActiveRuns.set(workflowId, workflowRuns);
      }

      return { activeRuns: newActiveRuns };
    });
  },

  clearWorkflowRuns: (workflowId: string) => {
    set(state => {
      const newActiveRuns = new Map(state.activeRuns);
      newActiveRuns.delete(workflowId);
      return { activeRuns: newActiveRuns };
    });
  },

  hasActiveRuns: (workflowId: string): boolean => {
    const state = get();
    const workflowRuns = state.activeRuns.get(workflowId);
    return workflowRuns ? workflowRuns.size > 0 : false;
  },

  getActiveRunCount: (workflowId: string): number => {
    const state = get();
    const workflowRuns = state.activeRuns.get(workflowId);
    return workflowRuns?.size ?? 0;
  },

  reset: () => {
    set(initialState);
  },
}));
